import dayjs from 'dayjs';
import i18next from 'i18n';
import { ExploitationContext } from 'utils/context';
import { useContext } from 'react';
import { CULTURE_TYPE } from 'utils/GlobalVariables';

const regexMail =
  /^(([^<>()[\],;:\s@]+(\.[^<>()[\],;:\s@]+)*)|(.+))@(([^<>()[\],;:\s@]+\.)+[^<>()[\],;:\s@]{2,})$/i;

function capitalizeFirstLetter(str) {
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
}

// Validator only number and not limit
export const inputNumberValidator = (field, nameField) => {
  if (field) {
    if (!new RegExp(/^\d{1,}$/).test(field)) {
      return i18next.t('validator.digitsOnly', { field: nameField });
    }
  }
  return null;
};

// Validator only positive integer and not limit
export const inputIntegerValidator = (field, nameField) => {
  if (field) {
    if (!Number.isInteger(+field) || Number(field) < 0) {
      return i18next.t('validator.positiveIntegers', { field: nameField });
    }
  }
  return null;
};

// Validator only positive float and not limit
export const inputPositiveFloatValidator = (field, nameField) => {
  if (field) {
    if (isNaN(field) || Number(field) < 0) {
      return i18next.t('validator.positiveNumbers', { field: nameField });
    }
  }
  return null;
};

// Validator limit number until 500
export const inputNitrogenValidator = (field, nameField) => {
  if (field > 500) {
    return i18next.t('validator.max500', { field: nameField });
  }
  return null;
};

// Validator only greater than 0 float and not limit
export const inputAbsolutePositiveFloatValidator = (field, nameField) => {
  if (field || field === 0) {
    if (isNaN(field) || Number(field) <= 0) {
      return i18next.t('validator.greaterThan0', { field: nameField });
    }
  }
  return null;
};

export const inputPositiveNotNullFloatValidator = (field, nameField) => {
  if ((!field && field !== 0) || isNaN(field) || Number(field) < 0) {
    return i18next.t('validator.greaterOrEqual0', { field: nameField });
  }
  return null;
};

// Validator limit number until 30
export const inputIFTValidator = (field, nameField) => {
  if (field > 30) {
    return i18next.t('validator.max30', { field: nameField });
  }
  return null;
};

// Validator any float number and not limit
export const inputAnyFloatValidator = (field, nameField) => {
  if (isNaN(field)) {
    return i18next.t('validator.numbersOnly', { field: nameField });
  }
  return null;
};

// Validator any float number and not limit
export const inputAnyFloatNotNullValidator = (field, nameField) => {
  if (isNaN(field) || (!field && field !== 0)) {
    return i18next.t('validator.singleNumber', { field: nameField });
  }
  return null;
};

// Validator any float number between -50000 and 50000
export const inputExtraCostValidator = (field, nameField) => {
  if (isNaN(field) || (!field && field !== 0) || field < -50000 || field > 50000) {
    return i18next.t('validator.between-50kAnd50k', { field: nameField });
  }
  return null;
};

// Validator any float number between 0 and 50000
export const inputYieldValidator = (field, nameField) => {
  if (field > 50000) {
    return i18next.t('validator.between0And50k', { field: nameField });
  }
  return null;
};

// Validator for presenceDuration and prohibitionDuration limit 10
export const inputDurationValidator = (field, nameField) => {
  if (nameField === 'Durée de présence') {
    if ((field && field === '0') || field > 10) {
      return i18next.t('validator.between1And10', { field: nameField });
    }
  }
  if (nameField === "Durée d'interdiction" && field > 10) {
    return i18next.t('validator.between0And10', { field: nameField });
  }
  return null;
};

// Validator any float number between 0 and 2000
export const inputIrrigationYieldGain = (field, nameField) => {
  if (isNaN(field) || (!field && field !== 0) || field < 0 || field > 2000) {
    return i18next.t('validator.between0And2000', { field: nameField });
  }
  return null;
};

// Validator for checking if the field is not empty
export const inputRequired = (field, nameField) => {
  if (!field) {
    return i18next.t('validator.required', { field: nameField });
  }
  return null;
};

// Validator for checking if the field value is unique
export const inputUnique = (field, nameField, params) => {
  if (params?.isValueAlreadyUsed) {
    return i18next.t('validator.notUnique', {
      field: nameField,
      value: capitalizeFirstLetter(field),
    });
  }
  return null;
};

// Validator for checking if the field is correctly defined
export const inputVerifyCode = (field, nameField) => {
  if (!field) {
    return i18next.t('validator.invalid', { field: nameField });
  }
  return null;
};

// Validator for checking field name limit
export const inputVerifyName = (field, nameField) => {
  if (field.length > 0 && field.length < 3) {
    return i18next.t('validator.minChars3', { field: nameField });
  }
  return null;
};

// Validator for checking if the field is not empty but 0 allowed
export const inputNumberRequired = (field, nameField) => {
  if (!field && field !== 0) {
    return i18next.t('validator.required', { field: nameField });
  }
  return null;
};

// Validator for check mail format
export const inputVerifyMail = (field) => {
  if (field) {
    if (regexMail.test(field) === false) {
      return i18next.t('validator.invalidEmail');
    }
  }
};

// Validator for check limit
export const inputIntegerValidatorEffect = (field, nameField) => {
  if (field) {
    if (Number(field) < 0) {
      return i18next.t('validator.notNegative', { field: nameField });
    }
    if (Number(field) > 500) {
      return i18next.t('validator.max500b', { field: nameField });
    }
  }
  return null;
};

// Validator for rotate effects
export const inputFloatValidatorRotateEffect = (field) => {
  if (!field && field !== 0) {
    return i18next.t('validator.rotationRequired');
  } else if (isNaN(field)) {
    return i18next.t('validator.rotationNumbersOnly');
  } else if (field) {
    if (Number(field) < 0) {
      return i18next.t('validator.rotationNotNegative');
    }
    if (Number(field) > 200) {
      return i18next.t('validator.rotationMax200');
    }
  }
  return null;
};

// Validator for check limit years simulation
export const inputYearNumberSimulation = (field, nameField) => {
  if (field) {
    if (Number(field) < 3) {
      return i18next.t('validator.min3', { field: nameField });
    }
    if (Number(field) > 7) {
      return i18next.t('validator.max7', { field: nameField });
    }
  }
  return null;
};

export const inputNumberStrictLengthValidatorOrNull = (number) => (field, nameField) => {
  if (field.length === 0) {
    return null;
  } else if (field.length !== number) {
    return i18next.t('validator.exactLengthOrEmpty', { field: nameField, number });
  } else {
    return inputNumberValidator(field, nameField);
  }
};

export const inputNumberStrictLengthValidator = (number) => (field, nameField) => {
  if (field.length !== number) {
    return i18next.t('validator.exactLength', { field: nameField, number });
  } else {
    return inputNumberValidator(field, nameField);
  }
};

export const inputStringMinLengthValidator = (min) => (field, nameField) => {
  if (field.length < min) {
    return i18next.t('validator.minLength', { field: nameField, min });
  }
  return null;
};

export const inputStringMinMaxLengthValidator = (min, max) => (field, nameField) => {
  if (field.length < min || field.length > max) {
    return i18next.t('validator.betweenLength', { field: nameField, min, max });
  }
  return null;
};

export const inputNumberMinMaxLengthValidatorOrNull = (min, max) => (field, nameField) => {
  if (field.length === 0) {
    return null;
  } else if (field.length < min || field.length > max) {
    return i18next.t('validator.betweenLengthOrEmpty', { field: nameField, min, max });
  } else {
    return inputNumberValidator(field, nameField);
  }
};

export const inputNumberMinMaxValidator = (min, max) => (field, nameField) => {
  //if "field" is not a number return error message else return null
  const errorMessageOrNull = inputNumberValidator(field, nameField);
  const hasError = errorMessageOrNull !== null;
  if (!hasError) {
    if (field < min || field > max) {
      return `Le champ ${nameField} doit être un nombre entre ${min} et ${max}`;
    }
  }
};

// Validator for checking if the date is not > to 31 jully of the current year
export const parcelConversionDateChecker = (startYear) => (field, nameField) => {
  const maxDate = dayjs().year(Number(startYear)).month(6).date(31);
  if (field) {
    if (dayjs(field).isAfter(maxDate)) {
      return i18next.t('validator.beforeJuly31', { field: nameField });
    }
    if (field === 'Invalid Date') {
      return i18next.t('validator.invalidField', { field: nameField });
    }
  }
};

export const inputVerifySeason = (field, nameField, params) => {
  if (params?.cultureType === CULTURE_TYPE.intermediate) {
    if (field === 'spring' || field === '') {
      return i18next.t('validator.invalidSeazonIntercrop');
    }
  }
};

export const inputVerifyCultureAndPresenceDuration = (field, nameField, params) => {
  const isNumberErrorMessage = inputIntegerValidator(field, nameField);
  if (isNumberErrorMessage !== null) {
    return isNumberErrorMessage;
  }
  if (Number(field) > 1) {
    if (params?.cultureSeason !== '') {
      return i18next.t('validator.invalidPresenceDurationAndSeazon');
    }
    if (params?.cultureType === CULTURE_TYPE.intermediate) {
      return i18next.t('validator.invalidPresenceDurationAndIntercrops');
    }
  }
};
